import React, { useRef } from 'react';
import { Button, Col, Container, Row } from 'react-bootstrap';
import { type GetServerSideProps } from 'next';
import { useRouter } from 'next/router';
import { createSelector } from '@reduxjs/toolkit';
import { useDispatch, useSelector } from 'react-redux';
import styles from '../../styles/OurBoxesItem.module.css';
import api from '../../lib/utils/api';
import cmsEndpoints from '../../lib/utils/cmsEndpoints';
import { mapBoxData } from '../../lib/utils/mappers/map-box-data';
import {
  type BoxData,
  type Locale,
  type OurBoxesItemPageData,
  type SubscriptionData,
  type BoxesResponse,
  type OurBoxesItemPageResponse,
  type SubscriptionsResponse,
  SubscriptionVariant,
  HowItWorksStepDTO,
  HowItWorksDTO,
  BoxItemData,
} from '../../lib/types';
import { PageHead } from '../../components/PageHead';
import { mapOurBoxesItemPageData } from '../../lib/utils/mappers/map-our-boxes-item-page-data';
import { mapSubscriptionData } from '../../lib/utils/mappers/map-subscription-data';
import { mapSubscriptionsIncludesData } from '../../lib/utils/mappers/map-subscriptions-includes-data';
import { HeaderWithDescription } from '../../components/HeaderWithDescription';
import { SubscriptionsList } from '../../components/SubscriptionsList';
import { RootState } from '../../store';
import { convergeTracking } from '../../lib/utils/converge';
import { addToCartRequest } from '../../store/actions/cart';
import { clickedCTA } from '../../store/actions/events';
import { ctaClickSources, LOCALES, PAGE_SECTIONS } from '../../lib/constants';
import { BoxCard } from '../../components/BoxCard';
import MagazineSlider from '../../components/MagazineSlider';
import PageSection from '../../components/PageSection';
import HowItWorksStep from '../../components/HowItWorksStep';
import { mapHowItWorksData } from '../../lib/utils/mappers';
import { scrollToRef } from '../../lib/utils/scroll-to-ref';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheck,
  faHeart,
  faRotateLeft,
  faShield,
  faArrowRight,
} from '@fortawesome/free-solid-svg-icons';
import CarouselOnMobile from '../../components/CarouselOnMobile';
import GalleryItem from '../../components/GalleryItem';
import { Image } from '../../lib/types/data/image';
import Gallery from '../../components/Gallery';
import BoxAvailabilty from '../../components/BoxAvailabilty';
import PICKING_ICON from '../../public/images/pickingVector.svg';
import VISA_LOGO from '../../public/images/visaLogo.svg';
import MASTERCARD_LOGO from '../../public/images/mastercardLogo.svg';
import PAYPAL_LOGO from '../../public/images/paypalLogo.svg';
import AMERCIANEXPRESS_LOGO from '../../public/images/americanExpressLogo.svg';
import { useAppContext } from '../../context/shared';
import CompanyReviews from '../../components/CompanyReviews';
import { GalleryItemData } from '../../lib/types/data/gallery-item-data';

const MAX_MONTHS_TARGET = 36;

export const getServerSideProps: GetServerSideProps = async (context) => {
  const locale = context.locale as Locale;
  const boxUrl = context.params?.box as string;

  const [boxesData, pageData, subscriptionData, homescreen] = await Promise.all(
    [
      api.get<BoxesResponse>(cmsEndpoints.boxes()),
      api.get<OurBoxesItemPageResponse>(cmsEndpoints.ourBoxesItemPage()),
      api.get<SubscriptionsResponse>(cmsEndpoints.subscriptions()),
      api.get(cmsEndpoints.homescreen()),
    ]
  );

  const homescreenAttributes = homescreen?.data.data.attributes ?? {};
  const blocks = homescreenAttributes.blocks ?? [];

  const howItWorksData = blocks.find(
    (block: any) => block.__component === PAGE_SECTIONS.HOW_IT_WORKS
  );

  const howItWorks: HowItWorksDTO = mapHowItWorksData(howItWorksData, locale);

  if (boxesData && pageData && subscriptionData) {
    const boxes = (boxesData?.data?.data ?? []).map(
      (box) => mapBoxData(box, locale) ?? {}
    );

    const selectedBox = boxes.find((box) => box.url === boxUrl);

    const page = mapOurBoxesItemPageData(
      pageData?.data?.data?.attributes,
      locale
    );

    const subscriptions = subscriptionData.data.data.attributes.options.map(
      (subscription) => mapSubscriptionData(subscription, locale)
    );
    const subscriptionsIncludes =
      subscriptionData.data.data.attributes.includes.map((item) =>
        mapSubscriptionsIncludesData(item, locale)
      );

    if (selectedBox) {
      let monthsTarget =
        selectedBox.ageGroup?.maxMonths === MAX_MONTHS_TARGET * 2
          ? MAX_MONTHS_TARGET
          : selectedBox.ageGroup?.maxMonths;
      const nextBoxes: BoxData[] = [];
      const MAX_BOXES = 3;
      const boxesSorted = Array.from(boxes).sort(
        (a, b) => (a.ageGroup?.minMonths ?? 0) - (b.ageGroup?.minMonths ?? 0)
      );

      boxesSorted.forEach((box) => {
        if (box.id === selectedBox.id || nextBoxes.length >= MAX_BOXES) return;

        if (box.ageGroup?.minMonths === monthsTarget) {
          nextBoxes.push(box);
          if (box.ageGroup?.minMonths !== MAX_MONTHS_TARGET) {
            monthsTarget = box.ageGroup?.maxMonths;
          }
        }
      });

      let toysFiltered = selectedBox.boxItems.filter(
        (boxItem) => !boxItem.isGuide
      );

      const toyList: GalleryItemData[] = toysFiltered.map((boxItem) => ({
        src: boxItem.shopifyImageUrl,
        alt: boxItem.title,
        description: boxItem.description,
        title: boxItem.title,
      }));

      toyList.unshift({
        src: selectedBox.overviewImage,
        alt: selectedBox.title,
      });

      return {
        props: {
          page,
          selectedBox,
          subscriptions,
          subscriptionsIncludes,
          nextBoxes,
          howItWorks,
          toyList,
        },
      };
    }
  }

  return {
    notFound: true,
  };
};

type OurBoxesItemProps = {
  readonly selectedBox: BoxData;
  readonly nextBoxes: BoxData[];
  readonly page: OurBoxesItemPageData;
  readonly subscriptions: SubscriptionData[];
  readonly subscriptionsIncludes: string[];
  readonly howItWorks: HowItWorksDTO;
  readonly toyList: Image[];
};

const PAYMENT_METHODS_LOGOS = [
  VISA_LOGO.src,
  AMERCIANEXPRESS_LOGO.src,
  MASTERCARD_LOGO.src,
  PAYPAL_LOGO.src,
];

function BoxBenefit(props: { readonly description: string }) {
  return (
    <div className={styles.boxBenefit}>
      <div className={`${styles.boxBenefitIcon} me-3 mb-1`}>
        <FontAwesomeIcon icon={faHeart} />
      </div>
      <div className={`${styles.boxBenefitText} ms-3 py-4`}>
        <div className="text-gray-60">{props.description}</div>
      </div>
    </div>
  );
}

function getBoxMonths({
  minMonths,
  maxMonths,
  locale,
}: {
  minMonths?: number;
  maxMonths?: number;
  locale: Locale;
}) {
  if (minMonths === undefined || maxMonths === undefined) {
    return '';
  }

  if (minMonths === MAX_MONTHS_TARGET) {
    return locale === LOCALES.de
      ? `Für Kinder ab ${minMonths} Monaten`
      : `For children from ${minMonths} months`;
  }

  const months: string[] = [];

  for (let i = minMonths; i < maxMonths; i++) {
    months.push(i.toString());
  }

  return months
    .map((month) => {
      return locale === LOCALES.de ? `Monate ${month}` : `${month} months`;
    })
    .join(', ');
}

function OurBoxesItem(props: OurBoxesItemProps) {
  const router = useRouter();
  const dispatch = useDispatch();
  const subscribeSectionRef = useRef(null);
  const { locale } = useAppContext();

  const { selectedBox } = props;

  const selectCart = (state: RootState) => state.cart;
  const selectCartMemoized = createSelector(selectCart, (cart) => cart);
  const cart = useSelector(selectCartMemoized);

  const handleOpenBox = (boxUrl: string) => {
    router.push(boxUrl).catch(() => {
      console.error('Failed to navigate');
    });
  };

  const handleGoBack = () => {
    router.push('../our-boxes').catch(() => {
      console.error('Failed to navigate');
    });
  };

  const handleSubscribeClick = (subscription: SubscriptionVariant) => {
    const variant = selectedBox.variantsJson[subscription];
    if (!variant?.id) return;
    convergeTracking.trackAddedToCart(selectedBox, subscription);
    dispatch(addToCartRequest({ id: variant.id, subscription }));
    dispatch(
      clickedCTA({
        name: ctaClickSources.subscriptionCard.name,
        url: router.pathname,
        subscription,
        shopifyId: selectedBox.shopifyId,
        boxTitle: selectedBox.title,
        variantId: variant.id,
      })
    );
    router.push('../cart').catch(() => {
      console.error('Failed to navigate');
    });
  };

  return (
    <>
      <PageHead
        title={selectedBox.title}
        description={selectedBox.metaDescription}
      />
      <div className={`${styles.recommendedBoxContainer} py-5`}>
        <Container>
          <Row>
            <Col>
              <h1 className="text-center pb-4">{selectedBox.title}</h1>
              <BoxAvailabilty availability={selectedBox.availability} />
            </Col>
          </Row>
          <div className={styles.boxWrapper}>
            <CarouselOnMobile
              desktopChildren={<Gallery items={props.toyList} />}
              mobileChildren={props.toyList.map((toy) => (
                <GalleryItem key={toy.src} data={toy} />
              ))}
            />
            <div className={styles.boxInfoWrapper}>
              <h2 className={styles.boxInfoTitle}>
                {locale === LOCALES.de
                  ? `Spielzeugbox ab ${selectedBox.ageGroup?.minMonths} Monate`
                  : `Toy box from ${selectedBox.ageGroup?.minMonths} months
								`}
              </h2>
              <p className={styles.boxMonths}>
                {getBoxMonths({
                  minMonths: selectedBox.ageGroup?.minMonths,
                  maxMonths: selectedBox.ageGroup?.maxMonths,
                  locale,
                })}
              </p>

              <CompanyReviews />
              <h2 className={styles.boxInfoTitle}>{props.page.boxInfoTitle}</h2>
              <ul className={styles.boxInfoList}>
                {selectedBox.boxContentList.map((item, index) => (
                  <li
                    className={styles.boxInfoListItem}
                    key={`${item.listLine_de}_${index}`}
                  >
                    <FontAwesomeIcon icon={faCheck} />
                    <span>
                      {locale === LOCALES.de
                        ? item.listLine_de
                        : item.listLine_en}
                    </span>
                  </li>
                ))}
              </ul>
              <div className={styles.buttonContainer}>
                <Button
                  className="w-100"
                  size="lg"
                  onClick={() => {
                    scrollToRef(subscribeSectionRef);
                  }}
                >
                  {props.page.subscribeButton}
                </Button>
                <div className={styles.paymentMethods}>
                  <p>{props.page.paymentMethodsTitle}</p>
                  <div className={styles.paymentMethodsLogos}>
                    {PAYMENT_METHODS_LOGOS.map((logo, index) => (
                      <img key={index} src={logo} alt="payment method logo" />
                    ))}
                  </div>
                </div>
                <div className={styles.shippingInfo}>
                  <div className={styles.shippingInfoItem}>
                    <div>
                      <img src={PICKING_ICON.src} alt="picking icon" />
                    </div>
                    <p>
                      {locale === LOCALES.de
                        ? 'Inkl. MwSt. und Versand*'
                        : 'Incl. VAT and shipping*'}
                    </p>
                  </div>
                  <div className={styles.shippingInfoItem}>
                    <div>
                      <FontAwesomeIcon icon={faShield} />
                    </div>
                    <p>
                      {locale === LOCALES.de
                        ? 'Mietversicherung inbegriffen'
                        : 'Rental insurance included'}
                    </p>
                  </div>
                  <div className={styles.shippingInfoItem}>
                    <div>
                      <FontAwesomeIcon icon={faRotateLeft} />
                    </div>
                    <p>
                      {locale === LOCALES.de
                        ? '14 Tage Rückgaberecht'
                        : '14 day return policy'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Container>
      </div>

      <div className={styles['benefits-separator']}></div>
      <div className={styles['benefits-wrapper']}>
        <div className={styles['benefits-image-wrapper']}>
          <img src={props.page.boxBenefitsImage} alt="" className="w-100" />
        </div>
        <div className={styles.benefits}>
          <h2 className="text-center">{props.page.benefitsTitle}</h2>
          <div className="d-flex  flex-column">
            {props.page.boxBenefits.map((benefit, index: number) => (
              <BoxBenefit key={index} description={benefit.description} />
            ))}
          </div>
        </div>
      </div>
      <Container>
        <div ref={subscribeSectionRef}>
          <PageSection title={props.page.subscribeTitle}>
            <SubscriptionsList
              subscriptions={props.subscriptions}
              isCartReady={!cart.ready || cart.loading}
              onSubscribeClick={handleSubscribeClick}
            />
          </PageSection>
        </div>
      </Container>
      <div className={styles.howItWorksContainer}>
        <PageSection title={props.howItWorks.title}>
          <CarouselOnMobile
            desktopChildren={
              <div className={styles.howItWorksSteps}>
                {props.howItWorks.howItWorksStep.map(
                  (step: HowItWorksStepDTO, index: number) => (
                    <HowItWorksStep
                      key={index}
                      icon={step.icon}
                      title={step.title}
                      description={step.description}
                    />
                  )
                )}
              </div>
            }
            mobileChildren={props.howItWorks.howItWorksStep.map(
              (step: HowItWorksStepDTO, index: number) => (
                <HowItWorksStep
                  key={index}
                  icon={step.icon}
                  title={step.title}
                  description={step.description}
                />
              )
            )}
          />
        </PageSection>
      </div>

      {/* TODO: FAQ IS NOT NEEDED AT THE MOMENT */}
      {/* <div>
          <HeaderWithDescription title="FAQ" className="text-center" />
          <div>
            <Accordion defaultActiveKey="0">
              <Accordion.Item eventKey="0">
                <Accordion.Header>Accordion Item #1</Accordion.Header>
                <Accordion.Body>
                  dolor in reprehenderit in voluptate velit esse cillum dolore
                  eu fugiat nulla pariatur. Excepteur sint occaecat cupidatat
                  non proident, sunt in culpa qui officia deserunt mollit anim
                  id est laborum.
                </Accordion.Body>
              </Accordion.Item>
              <Accordion.Item eventKey="1">
                <Accordion.Header>Accordion Item #2</Accordion.Header>
                <Accordion.Body>
                  Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed
                  do eiusmod tempor incididunt ut labore et dolore magna aliqua.
                  Ut enim ad minim veniam, quis nostrud exercitation ullamco
                </Accordion.Body>
              </Accordion.Item>
            </Accordion>
          </div>
        </div> */}
      {props.nextBoxes?.length && (
        <div className={styles['next-boxes']}>
          <Container>
            <HeaderWithDescription
              title={props.page.swapsTitle}
              description={props.page.swapsDescription}
              className="text-center"
            />
            <div className="my-5">
              <CarouselOnMobile
                desktopChildren={
                  <div className="d-flex justify-content-center gap-5 flex-wrap">
                    {props.nextBoxes.map((box) => (
                      <BoxCard
                        key={box.id}
                        box={box}
                        onClick={() => {
                          handleOpenBox(box.url);
                        }}
                      />
                    ))}
                  </div>
                }
                mobileChildren={props.nextBoxes.map((box) => (
                  <BoxCard
                    key={box.id}
                    box={box}
                    onClick={() => handleOpenBox(box.url)}
                  />
                ))}
              />
            </div>
          </Container>
          <div className={styles.button} onClick={handleGoBack}>
            <div className="d-inline-flex align-items-center gap-2 clickable">
              <div className="text-decoration-underline">
                {props.page.exploreAllBoxesButton}
              </div>
              <FontAwesomeIcon icon={faArrowRight} />
            </div>
          </div>
        </div>
      )}
      <MagazineSlider title={props.page.magazinesTitle} />
    </>
  );
}

export default OurBoxesItem;
